/**
 * @file tabs-orders.tsx
 * @module app/modules/sales-management/sales-manager/sales-representative/tabs/tabs-orders
 * @description Implements the tabs orders interface for sales-management operations.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import { useEffect, useState } from 'react';
import { Button, Card, Col, Row } from 'react-bootstrap';
import { useNavigate } from 'react-router-dom';
import b1 from "../../../../../../_admin/assets/media/product/default.svg";
import { KTSVG } from '../../../../../../_admin/helpers';
import APICallService from '../../../../../../api/apiCallService';
import { ordersDelivery } from '../../../../../../api/apiEndPoints';
import { getKey, setKey } from '../../../../../../Global/history';
import Loader from '../../../../../../Global/loader';
import Pagination from '../../../../../../Global/pagination';
import { Manager, OrderCancelled, OrderDelivered, OrderOutForDelivery, OrderProcessed, PAGE_LIMIT, ServiceMember } from '../../../../../../utils/constants';
import Method from '../../../../../../utils/methods';
import { orderStatusJSON, REPRESENTATIVE_TARGET_PERIOD } from '../../../../../../utils/staticJSON';
import { listSalesRepresentativeOrders } from '../../../../../../utils/storeString';
import { CustomSelect } from '../../../../../custom/Select/CustomSelect';
import { useAuth } from '../../../../auth';
import { useNavigationHistory } from '../../../../../../utils/useNavigationHistory';
const TabsOrders = (props: any) => {
  const { currentUser }: any = useAuth();
  const navigate = useNavigate();
  const { navigateTo } = useNavigationHistory();
  const getStatusBadge = (status: any) => {
    const statusObj = orderStatusJSON.find((val) => val.value === status);
    if (!statusObj)
      return (
        <div className="badge rounded-pill bg-light-secondary text-secondary fs-14 p-4">
          NA
        </div>
      );

    let badgeClass = "bg-light-primary text-primary";
    switch (status) {
      case OrderProcessed:
        badgeClass = "bg-light-warning text-warning";
        break;
      case OrderOutForDelivery:
        badgeClass = "bg-light-info text-info";
        break;
      case OrderDelivered:
        badgeClass = "bg-light-success text-success";
        break;
      case OrderCancelled:
        badgeClass = "bg-light-danger text-danger";
        break;
      default:
        badgeClass = "bg-light-primary text-primary";
    }

    return (
      <div className={`badge rounded-pill ${badgeClass} fs-14 py-2 px-4`}>
        {statusObj.label}
      </div>
    );
  };
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState<any>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [fetchLoader, setFetchLoader] = useState(true);
  const [page, setPage] = useState<any>(
    getKey(listSalesRepresentativeOrders.page) || 1
  );
  const [pageLimit, setPageLimit] = useState<any>(
    getKey(listSalesRepresentativeOrders.limit) || PAGE_LIMIT
  );
  const [status, setStatus] = useState<any>(
    getKey(listSalesRepresentativeOrders.status)
      ? Number(getKey(listSalesRepresentativeOrders.status))
      : null
  );
  const [district, setDistrict] = useState<any>(() => {
    const val: any = getKey(listSalesRepresentativeOrders.districts);
    if (!val || val === "null") return "";
    try {
      return JSON.parse(val);
    } catch {
      return val;
    }
  });
  const [districtsOptions, setDistrictsOptions] = useState<any>([]);
  const [startDate, setStartDate] = useState<any>(
    getKey(listSalesRepresentativeOrders.filterStartDate)
      ? new Date(
        getKey(listSalesRepresentativeOrders.filterStartDate)?.toString() || ""
      )
      : null
  );
  const [endDate, setEndDate] = useState<any>(
    getKey(listSalesRepresentativeOrders.filterEndDate)
      ? new Date(
        getKey(listSalesRepresentativeOrders.filterEndDate)?.toString() || ""
      )
      : null
  );
  const [search, setSearch] = useState<any>(
    getKey(listSalesRepresentativeOrders.search) || ""
  );
  useEffect(() => {
    (async () => {
      setFetchLoader(true);
      let start, end;
      if (props?.salesTarget?.startDate && props?.salesTarget?.endDate) {
        start = new Date(props.salesTarget.startDate);
        end = new Date(props.salesTarget.endDate);
        setStartDate(start);
        setEndDate(end);
        setKey(listSalesRepresentativeOrders.filterStartDate, start, false);
        setKey(listSalesRepresentativeOrders.filterEndDate, end, false);
      }
      await fetchOrdersList(
        page,
        pageLimit,
        status ? [status] : [],
        search,
        start,
        end,
        district
      );
      setFetchLoader(false);
    })();
  }, [props.salesTarget]);

  useEffect(() => {
    (async () => {
      const mappedDistricts = currentUser?.districts?.map((d: any) => ({
        value: d.reference,
        label: d.name || d.title,
        title: d.name || d.title,
      }));
      setDistrictsOptions(mappedDistricts);
    })();
  }, [currentUser]);

  const fetchOrdersList = async (
    pageNo: number,
    limit: number,
    statusList?: any,
    search?: string,
    fromDate?: Date,
    toDate?: Date,
    district?: any
  ) => {
    setLoading(true);
    let params: any = {
      pageNo: pageNo,
      limit: limit,
      sortKey: "_createdAt",
      sortOrder: -1,
      needCount: true,
      searchTerm: search ? search : "",
      fromDate: fromDate ? Method.convertDateToFormat(fromDate, "YYYY-MM-DD") : "",
      toDate: toDate ? Method.convertDateToFormat(toDate, "YYYY-MM-DD") : "",
      "assignedTo[0]": props.representativeId
    };
    if (statusList && statusList.length > 0) {
      statusList.map((val: any) => {
        params = { ...params, listType: val, status: val };
      });
    }
    if (district) {
      params["districts[0]"] = district;
    } else {
      if (currentUser?.districts && currentUser?.districts.length) {
        currentUser?.districts.map((d: any, index: number) => {
          params[`districts[${index}]`] = d.reference;
        });
      }
    }
    let endpoint = ordersDelivery.listAssistAdmin;
    //  if (
    //    currentUser?.role.some(
    //      (obj: any) => obj.type === Manager || obj.type === ServiceMember
    //    )
    //  ) {
    //    endpoint = ordersDelivery.listAssistAdmin;
    //  }
    let apiService = new APICallService(
      endpoint,
      params,
      "",
      "",
      "",
      "",
      "",
      ""
    );
    let response: any = await apiService.callAPI();
    if (response.records) {
      if (response.total) {
        setTotalRecords(response.total);
      } else {
        setTotalRecords(0);
      }
      setOrders(response.records);
    }
    setLoading(false);
  };
  const handleCurrentPage = async (val: number) => {
    setFetchLoader(true);
    setPage(val);
    setKey(listSalesRepresentativeOrders.page, val);
    await fetchOrdersList(
      val,
      pageLimit,
      status ? [status] : [],
      search,
      startDate,
      endDate,
      district
    );
    setFetchLoader(false);
  };
  const handleNextPage = async (val: number) => {
    setFetchLoader(true);
    setPage(val + 1);
    setKey(listSalesRepresentativeOrders.page, val + 1);
    await fetchOrdersList(
      val + 1,
      pageLimit,
      status ? [status] : [],
      search,
      startDate,
      endDate,
      district
    );
    setFetchLoader(false);
  };
  const handlePreviousPage = async (val: number) => {
    setFetchLoader(true);
    setPage(val - 1);
    setKey(listSalesRepresentativeOrders.page, val - 1);
    await fetchOrdersList(
      val - 1,
      pageLimit,
      status ? [status] : [],
      search,
      startDate,
      endDate,
      district
    );
    setFetchLoader(false);
  };
  const handlePageLimit = async (event: any) => {
    const limit = parseInt(event.target.value);
    setFetchLoader(true);
    setPage(1);
    setPageLimit(limit);
    setKey(listSalesRepresentativeOrders.page, 1);
    setKey(listSalesRepresentativeOrders.limit, limit);
    await fetchOrdersList(
      1,
      limit,
      status ? [status] : [],
      search,
      startDate,
      endDate,
      district
    );
    setFetchLoader(false);
  };
  const handleDateFilter = async (event: any) => {
    setStartDate(event[0]);
    setEndDate(event[1]);
    setFetchLoader(true);
    setPage(1);
    setKey(listSalesRepresentativeOrders.filterStartDate, event[0], false);
    setKey(listSalesRepresentativeOrders.filterEndDate, event[1], false);
    setKey(listSalesRepresentativeOrders.page, 1);
    if (event[0] && event[1]) {
      await fetchOrdersList(
        1,
        pageLimit,
        status ? [status] : [],
        search,
        event[0],
        event[1],
        district
      );
    }
    setFetchLoader(false);
  };
  const handleSearch = async (value: any) => {
    setSearch(value);
    setFetchLoader(true);
    setPage(1);
    setKey(listSalesRepresentativeOrders.search, value, false);
    setKey(listSalesRepresentativeOrders.page, 1);
    await fetchOrdersList(
      1,
      pageLimit,
      status ? [status] : [],
      value,
      startDate,
      endDate,
      district
    );
    setFetchLoader(false);
  };
  const handleStatusFilter = async (value: any) => {
    setStatus(value);
    setFetchLoader(true);
    setKey(listSalesRepresentativeOrders.status, value);
    setKey(listSalesRepresentativeOrders.page, 1);
    setLoading(true);
    setPage(1);
    await fetchOrdersList(
      1,
      pageLimit,
      value ? [value] : [],
      search,
      startDate,
      endDate,
      district
    );
    setFetchLoader(false);
  };
  const handleDistrictFilter = async (value: any) => {
    setDistrict(value);
    setKey(listSalesRepresentativeOrders.districts, value, false);
    setKey(listSalesRepresentativeOrders.page, 1);
    setFetchLoader(true);
    setPage(1);
    await fetchOrdersList(1, pageLimit, status, search, startDate, endDate, value);
    setFetchLoader(false);
  };
  return (
    <>
      <div className="mb-8 d-flex justify-content-between flex-wrap align-items-center gap-5">
        <div className="card-title">
          <h2 className="fs-22 fw-bolder">{totalRecords ?? 0} Orders</h2>
        </div>
        <Row className="align-items-center gy-5">
          <Col sm={12} md={6} lg={4}>
            <div className="d-flex align-items-center position-relative">
              <KTSVG
                path="/media/icons/duotune/general/gen021.svg"
                className="svg-icon-3 position-absolute ms-3"
              />
              <input
                type="text"
                id="kt_filter_search"
                className="form-control form-control-custom borde-r8px bg-white ps-11"
                placeholder="Search by ID/customer name"
                value={search}
                onChange={(event: any) => {
                  handleSearch(event.target.value);
                }}
              />
            </div>
          </Col>
          <Col sm={12} md={6} lg={4}>
            <div>
              <CustomSelect
                backgroundColor="#ffff"
                value={orderStatusJSON.filter(option => option.value !== 3).find(
                  (option) => option?.value == status
                )}
                options={orderStatusJSON.filter(option => option.value !== 3)}
                onChange={(event: any) =>
                  handleStatusFilter(event ? event.value : null)
                }
                placeholder="Filter by status"
                isSearchable={false}
                isMulti={false}
                isClearable={true}
              />
            </div>
          </Col>
          <Col sm={12} md={6} lg={4}>
            <div>
              <CustomSelect
                backgroundColor="#ffff"
                value={districtsOptions.find(
                  (option: any) => option?.value == district
                )}
                options={districtsOptions}
                onChange={(event: any) =>
                  handleDistrictFilter(event ? event.value : null)
                }
                placeholder="Filter by district"
                isSearchable={false}
                isMulti={false}
                isClearable={true}
              />
            </div>
          </Col>
        </Row>
      </div>
      {!fetchLoader ? (
        orders && orders.length ? (
          <>
            <Card className="border border-r10px">
              <Card.Body className="p-0">
                <div className="table-responsive">
                  <table className="table table-rounded table-row-bordered align-middle gs-7 gy-6 mb-0">
                    <thead>
                      <tr className="fw-bold fs-16 fw-600 text-dark border-bottom align-middle">
                        <th className="min-w-md-175px min-w-150px">
                          Order Date & ID
                        </th>
                        <th className="min-w-md-200px min-w-125px">
                          Customer name
                        </th>
                        {currentUser?.role.length ? (
                          currentUser?.role.some(
                            (obj: any) =>
                              obj.type !== Manager ||
                              obj.type !== ServiceMember
                          ) ? (
                            <></>
                          ) : (
                            <th className="min-w-140px">Ordered by</th>
                          )
                        ) : (
                          <></>
                        )}
                        <th className="min-w-md-125px">Rep Name</th>
                        <th className="min-w-md-125px min-w-100px">
                          Total amount
                        </th>
                        <th className="min-w-md-75px min-w-50px">Status</th>
                        <th className="min-w-100px text-end"></th>
                      </tr>
                    </thead>
                    <tbody>
                      <>
                        {orders.map((orderVal: any, goodIndex: number) => (
                          <tr key={goodIndex}>
                            <td className="fs-15 fw-500">
                              <div className="d-flex align-items-start flex-column">
                                <span className="text-dark d-block">
                                  {Method.convertDateToDDMMYYYYHOURS(
                                    orderVal._createdAt
                                  )}
                                </span>
                                <span className="text-gray d-block">
                                  {orderVal.refKey}
                                </span>
                              </div>
                            </td>
                            <td>
                              {/* <div className="symbol symbol-50px border position-relative">
                                <img
                                  className="img-fluid border-r8px object-fit-contain"
                                  src={
                                    orderVal.customer.image
                                      ? orderVal.customer.image
                                      : b1
                                  }
                                  alt=""
                                />
                              </div> */}
                              <span className="fs-15 fw-600 ms-3">
                                {orderVal.customer.name
                                  ? orderVal.customer.name
                                  : "-"}
                              </span>
                            </td>
                            <td>
                              <span className="fs-15 fw-500 badge rounded-pill bg-light-secondary text-black p-4 h-10">
                                {orderVal?.customer?.assignedTo?.name ?? "NA"}
                              </span>
                            </td>
                            <td>
                              <span className="fs-15 fw-500">
                                {"TSh " +
                                  Method.getGeneralizedAmount(
                                    orderVal.payment.totalCharge
                                  )}
                              </span>
                            </td>
                            <td>{getStatusBadge(orderVal.status)}</td>
                            <td className="text-end">
                              <Button
                                className="uw-btn uw-btn-primary fs-14 fw-600"
                                onClick={() => {
                                  let currentScrollY = window.scrollY;
                                  setKey(
                                    listSalesRepresentativeOrders.scrollPosition,
                                    currentScrollY
                                  );
                                  setKey(
                                    listSalesRepresentativeOrders.search,
                                    search,
                                    false
                                  );
                                  setKey(
                                    listSalesRepresentativeOrders.filterStartDate,
                                    startDate,
                                    false
                                  );
                                  setKey(
                                    listSalesRepresentativeOrders.filterEndDate,
                                    endDate,
                                    false
                                  );
                                  navigateTo("/orders/order-details", {
                                    state: {
                                      id: orderVal._id,
                                    },
                                  });
                                }}
                              >
                                View details
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </>
                    </tbody>
                  </table>
                </div>
              </Card.Body>
            </Card>
            {!loading && orders && orders.length ? (
              <Pagination
                totalRecords={totalRecords}
                currentPage={page}
                handleCurrentPage={handleCurrentPage}
                handleNextPage={handleNextPage}
                handlePreviousPage={handlePreviousPage}
                handlePageLimit={handlePageLimit}
                pageLimit={pageLimit}
              />
            ) : (
              <></>
            )}
          </>
        ) : (
          <>
            {startDate && endDate ? (
              <div className="border border-r10px mb-6">
                <div className="d-flex justify-content-center text-center align-items-center min-h-160px px-3">
                  <span className="fs-18 fw-500">No Data Found</span>
                </div>
              </div>
            ) : (
              <div className="border border-r10px mb-6">
                <div className="d-flex justify-content-center text-center align-items-center min-h-160px px-3">
                  <span className="fs-18 fw-500">
                    New orders / Unprocessed orders will be shown in this
                    section.
                  </span>
                </div>
              </div>
            )}
          </>
        )
      ) : (
        <div className="border border-r10px mb-6">
          <div className="d-flex justify-content-center text-center align-items-center min-h-160px px-3">
            <Loader loading={fetchLoader || loading} />
          </div>
        </div>
      )}
    </>
  );
};
export default TabsOrders;
